import {
  PublicKey,
  SYSVAR_CLOCK_PUBKEY,
  TransactionInstruction,
} from "@solana/web3.js";

import * as BufferLayout from "@solana/buffer-layout";
import * as Layout from "../../serialization/layout";
import { LendingInstruction } from "./instruction";
import { TOKEN_PROGRAM_ID } from "@solana/spl-token";
import { AccessType, getAccess } from "../../utils/Instructions";
import BN from "bn.js";
import { PORT_LENDING } from "../../constants";

// Deposit liquidity into a reserve in exchange for collateral. Collateral represents a share
// of the reserve liquidity pool.
//
// Accounts expected by this instruction:
//
//   0. `[writable]` Source liquidity token account.
//                     $authority can transfer $liquidity_amount.
//   1. `[writable]` Destination collateral token account.
//   2. `[writable]` Reserve account.
//   3. `[writable]` Reserve liquidity supply SPL Token account.
//   4. `[writable]` Reserve collateral SPL Token mint.
//   5. `[]` Lending market account.
//   6. `[]` Derived lending market authority.
//   7. `[signer]` User transfer authority ($authority).
//   8. `[]` Clock sysvar.
//   9. `[]` Token program id.
export const depositReserveLiquidityInstruction = (
  liquidityAmount: number | BN,
  srcLiquidityPubkey: PublicKey, // 0
  dstCollateralPubkey: PublicKey, // 1
  reservePubkey: PublicKey, // 2
  reserveLiquiditySupplyPubkey: PublicKey, // 3
  reserveCollateralMintPubkey: PublicKey, // 4
  lendingMarketPubkey: PublicKey, // 5
  lendingMarketAuthorityPubkey: PublicKey, // 6
  transferAuthorityPubkey: PublicKey, // 7
  lendingProgramId: PublicKey = PORT_LENDING
): TransactionInstruction => {
  const dataLayout = BufferLayout.struct([
    BufferLayout.u8("instruction"),
    Layout.uint64("liquidityAmount"),
  ]);
  const data = Buffer.alloc(dataLayout.span);
  dataLayout.encode(
    {
      instruction: LendingInstruction.DepositReserveLiquidity,
      liquidityAmount: new BN(liquidityAmount),
    },
    data
  );

  const keys = [
    getAccess(srcLiquidityPubkey, AccessType.WRITE),
    getAccess(dstCollateralPubkey, AccessType.WRITE),
    getAccess(reservePubkey, AccessType.WRITE),
    getAccess(reserveLiquiditySupplyPubkey, AccessType.WRITE),
    getAccess(reserveCollateralMintPubkey, AccessType.WRITE),
    getAccess(lendingMarketPubkey, AccessType.READ),
    getAccess(lendingMarketAuthorityPubkey, AccessType.READ),
    getAccess(transferAuthorityPubkey, AccessType.SIGNER),
    getAccess(SYSVAR_CLOCK_PUBKEY, AccessType.READ),
    getAccess(TOKEN_PROGRAM_ID, AccessType.READ),
  ];

  return new TransactionInstruction({
    keys,
    programId: lendingProgramId,
    data,
  });
};
